import type { PageLink } from "../types";

export const SHOP_SEARCH_MIN_QUERY_LENGTH = 2;
export const SHOP_SEARCH_DEFAULT_LIMIT = 6;
export const SHOP_SEARCH_MAX_LIMIT = 24;

export type ShopSearchScope = "everything" | "pages" | "products" | "categories";

export type ShopSearchSuggestionProduct = {
    uuid: string;
    name: string;
    slug: string | null;
    reference: string | null;
    price: string;
    manufacturerUuid: string | null;
    manufacturerName: string | null;
    isOnSale: boolean;
    availableForOrder: boolean;
    trackInventory: boolean;
    availableQuantity: number | null;
};

export type ShopSearchSuggestionCategory = {
    uuid: string;
    name: string;
    slug: string | null;
    pathNames: string[];
};

export type ShopSearchSuggestionPage = PageLink & {
    href: string;
};

export type ShopSearchSuggestionsPayload = {
    query: string;
    products: ShopSearchSuggestionProduct[];
    categories: ShopSearchSuggestionCategory[];
    productTotal: number;
    categoryTotal: number;
};

export type ShopSearchSuggestionGroups = {
    query: string;
    scope: ShopSearchScope;
    limit: number;
    pages: ShopSearchSuggestionPage[];
    products: ShopSearchSuggestionProduct[];
    categories: ShopSearchSuggestionCategory[];
    productTotal: number;
    categoryTotal: number;
};

export type ShopSearchHrefResolver = (href: string) => string;

export type SelectShopSearchSuggestionsOptions = {
    query: string;
    scope?: unknown;
    limit?: unknown;
    pages?: readonly PageLink[];
    payload?: unknown;
    resolveHref?: ShopSearchHrefResolver;
};

function stringOrNull(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim();

    return normalized === "" ? null : normalized;
}

function numberOrNull(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringList(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item) => {
        const normalized = stringOrNull(item);

        return normalized === null ? [] : [normalized];
    });
}

function parseProducts(value: unknown): ShopSearchSuggestionProduct[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): ShopSearchSuggestionProduct[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);
        const price = stringOrNull(data.price);

        if (uuid === null || name === null || price === null) {
            return [];
        }

        return [{
            uuid,
            name,
            slug: stringOrNull(data.slug),
            reference: stringOrNull(data.reference),
            price,
            manufacturerUuid: stringOrNull(data.manufacturerUuid),
            manufacturerName: stringOrNull(data.manufacturerName),
            isOnSale: data.isOnSale === true,
            availableForOrder: data.availableForOrder !== false,
            trackInventory: data.trackInventory === true,
            availableQuantity: numberOrNull(data.availableQuantity),
        }];
    });
}

function parseCategories(value: unknown): ShopSearchSuggestionCategory[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): ShopSearchSuggestionCategory[] => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return [];
        }

        const data = item as Record<string, unknown>;
        const uuid = stringOrNull(data.uuid);
        const name = stringOrNull(data.name);

        if (uuid === null || name === null) {
            return [];
        }

        return [{
            uuid,
            name,
            slug: stringOrNull(data.slug),
            pathNames: stringList(data.pathNames),
        }];
    });
}

export function normalizeShopSearchScope(
    value: unknown,
    fallback: ShopSearchScope = "everything",
): ShopSearchScope {
    return value === "pages" || value === "products" || value === "categories" || value === "everything"
        ? value
        : fallback;
}

export function normalizeShopSearchSuggestionLimit(
    value: unknown,
    fallback = SHOP_SEARCH_DEFAULT_LIMIT,
): number {
    const parsed = value === null || value === undefined || value === ""
        ? Number.NaN
        : typeof value === "number" ? value : Number(value);
    const parsedFallback = Number.isFinite(fallback) ? fallback : SHOP_SEARCH_DEFAULT_LIMIT;
    const limit = Number.isFinite(parsed) ? parsed : parsedFallback;

    return Math.max(1, Math.min(SHOP_SEARCH_MAX_LIMIT, Math.trunc(limit)));
}

export function shopSearchScopeUsesCatalog(scope: ShopSearchScope): boolean {
    return scope !== "pages";
}

export function parseShopSearchSuggestions(value: unknown): ShopSearchSuggestionsPayload {
    const data = value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
    const products = parseProducts(data.products);
    const categories = parseCategories(data.categories);

    return {
        query: stringOrNull(data.query) ?? "",
        products,
        categories,
        productTotal: numberOrNull(data.productTotal) ?? products.length,
        categoryTotal: numberOrNull(data.categoryTotal) ?? categories.length,
    };
}

export function shopSearchPageHref(
    page: PageLink,
    resolveHref: ShopSearchHrefResolver = (href) => href,
): string {
    const configuredHref = stringOrNull(page.href);

    if (configuredHref !== null) {
        return resolveHref(configuredHref);
    }

    const slug = page.slug.trim().replace(/^\/+/, "");
    const href = page.isHomepage || slug === "" ? "/" : `/${slug}`;

    return resolveHref(href);
}

export function shopSearchPageMatches(
    pages: readonly PageLink[],
    query: string,
    rawLimit: unknown = SHOP_SEARCH_DEFAULT_LIMIT,
    resolveHref?: ShopSearchHrefResolver,
): ShopSearchSuggestionPage[] {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    if (normalizedQuery.length < SHOP_SEARCH_MIN_QUERY_LENGTH) {
        return [];
    }

    const limit = normalizeShopSearchSuggestionLimit(rawLimit);

    return pages
        .filter((page) => page.title.trim() !== "")
        .filter((page) => (
            `${page.title} ${page.slug} ${page.pageType ?? ""} ${page.templateKey ?? ""}`
                .toLocaleLowerCase()
                .includes(normalizedQuery)
        ))
        .slice(0, limit)
        .map((page) => ({
            ...page,
            href: shopSearchPageHref(page, resolveHref),
        }));
}

export function selectShopSearchSuggestions({
    query,
    scope: rawScope,
    limit: rawLimit,
    pages = [],
    payload,
    resolveHref,
}: SelectShopSearchSuggestionsOptions): ShopSearchSuggestionGroups {
    const normalizedQuery = query.trim();
    const scope = normalizeShopSearchScope(rawScope);
    const limit = normalizeShopSearchSuggestionLimit(rawLimit);
    const parsed = parseShopSearchSuggestions(payload);
    const queryIsSearchable = normalizedQuery.length >= SHOP_SEARCH_MIN_QUERY_LENGTH;
    const includesPages = scope === "everything" || scope === "pages";
    const includesProducts = scope === "everything" || scope === "products";
    const includesCategories = scope === "everything" || scope === "categories";

    return {
        query: normalizedQuery,
        scope,
        limit,
        pages: queryIsSearchable && includesPages
            ? shopSearchPageMatches(pages, normalizedQuery, limit, resolveHref)
            : [],
        products: queryIsSearchable && includesProducts ? parsed.products.slice(0, limit) : [],
        categories: queryIsSearchable && includesCategories ? parsed.categories.slice(0, limit) : [],
        productTotal: queryIsSearchable && includesProducts ? parsed.productTotal : 0,
        categoryTotal: queryIsSearchable && includesCategories ? parsed.categoryTotal : 0,
    };
}

function searchHrefWithParams(searchHref: string, parameters: URLSearchParams): string {
    const hashIndex = searchHref.indexOf("#");
    const hash = hashIndex >= 0 ? searchHref.slice(hashIndex) : "";
    const hrefWithoutHash = hashIndex >= 0 ? searchHref.slice(0, hashIndex) : searchHref;
    const separator = hrefWithoutHash.includes("?") ? "&" : "?";

    return `${hrefWithoutHash}${separator}${parameters.toString()}${hash}`;
}

export function shopSearchResultsHref(
    searchHref: string,
    query: string,
    scope: ShopSearchScope = "everything",
): string {
    const parameters = new URLSearchParams({ q: query.trim() });

    if (scope !== "everything") {
        parameters.set("scope", scope);
    }

    return searchHrefWithParams(searchHref, parameters);
}

export function shopSearchProductHref(
    product: ShopSearchSuggestionProduct,
    searchHref: string,
    resolveHref: ShopSearchHrefResolver = (href) => href,
): string {
    return product.slug
        ? resolveHref(`/${product.slug.replace(/^\/+/, "")}`)
        : shopSearchResultsHref(searchHref, product.name, "products");
}

export function shopSearchCategoryHref(
    category: ShopSearchSuggestionCategory,
    searchHref: string,
    resolveHref: ShopSearchHrefResolver = (href) => href,
): string {
    return category.slug
        ? resolveHref(`/${category.slug.replace(/^\/+/, "")}`)
        : shopSearchResultsHref(searchHref, category.name, "categories");
}
